import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, TrendingUp, TrendingDown, AlertTriangle, Bell, BarChart3, History } from "lucide-react";
import { QualityCard, QualityOverallBanner } from "@/components/Quality";

export default function Calculadora() {
  const [params, setParams] = useState({ milk_density_kg_per_l: 1.030, curd_yield_pct: 9.5 });
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState({
    liters: "37116", weight_kg: "", fat_pct: "3.4", protein_pct: "", ph: "6.6", acidity: "", temperature_c: "4.5",
    cream_price: "80", curd_price: "42",
    cost_unit: "liter", unit_cost: "4.85",
    purchase_cost: "180000", curd_yield_pct: "9.5",
    correction_cost_estimate: "", supplier_id: "",
  });
  const [phInfo, setPhInfo] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/parameters"), api.get("/suppliers")]).then(([p, s]) => {
      setParams(p.data);
      setSuppliers(s.data);
      setForm((f) => ({
        ...f,
        weight_kg: f.liters ? (parseFloat(f.liters) * p.data.milk_density_kg_per_l).toFixed(2) : "",
        curd_yield_pct: String(p.data.curd_yield_pct),
      }));
    });
  }, []);

  // pH conversion on mount
  useEffect(() => {
    if (form.ph) computePh(form.ph);
    // eslint-disable-next-line
  }, []);

  // Recompute purchase_cost when unit_cost or quantities change
  useEffect(() => {
    if (form.cost_unit === "total") return;
    const unit = parseFloat(form.unit_cost);
    if (isNaN(unit)) return;
    let total = 0;
    if (form.cost_unit === "liter" && form.liters) total = parseFloat(form.liters) * unit;
    if (form.cost_unit === "kg" && form.weight_kg) total = parseFloat(form.weight_kg) * unit;
    if (total) setForm((f) => ({ ...f, purchase_cost: total.toFixed(2) }));
  }, [form.cost_unit, form.unit_cost, form.liters, form.weight_kg]);

  const computePh = async (v) => {
    if (!v || isNaN(parseFloat(v))) { setPhInfo(null); return; }
    try {
      const { data } = await api.get(`/conversion/ph-to-acidity?ph=${parseFloat(v)}`);
      setPhInfo(data);
      setForm((f) => ({ ...f, acidity: data.acidity.toString() }));
    } catch {}
  };

  const onLitersChange = (v) => {
    let weight = form.weight_kg;
    if (v && !isNaN(parseFloat(v))) weight = (parseFloat(v) * params.milk_density_kg_per_l).toFixed(2);
    else if (v === "") weight = "";
    setForm({ ...form, liters: v, weight_kg: weight });
  };
  const onKgChange = (v) => {
    let liters = form.liters;
    if (v && !isNaN(parseFloat(v))) liters = (parseFloat(v) / params.milk_density_kg_per_l).toFixed(2);
    else if (v === "") liters = "";
    setForm({ ...form, weight_kg: v, liters });
  };
  const onPhChange = (v) => { setForm({ ...form, ph: v }); computePh(v); };

  const compute = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/calculator/intelligence", {
        liters: parseFloat(form.liters),
        fat_pct: parseFloat(form.fat_pct),
        protein_pct: form.protein_pct ? parseFloat(form.protein_pct) : null,
        ph: form.ph ? parseFloat(form.ph) : null,
        acidity: form.acidity ? parseFloat(form.acidity) : null,
        temperature_c: form.temperature_c ? parseFloat(form.temperature_c) : null,
        cream_price: parseFloat(form.cream_price),
        curd_price: parseFloat(form.curd_price),
        purchase_cost: parseFloat(form.purchase_cost) || 0,
        cost_unit: form.cost_unit,
        unit_cost: form.unit_cost ? parseFloat(form.unit_cost) : null,
        curd_yield_pct: parseFloat(form.curd_yield_pct),
        correction_cost_estimate: form.correction_cost_estimate ? parseFloat(form.correction_cost_estimate) : null,
        supplier_id: form.supplier_id || null,
      });
      setResult(data);
    } finally { setLoading(false); }
  };

  const decisionColor = (d) => d === "COMPRAR" ? "bg-emerald-700 text-white"
    : d === "NEGOCIAR PRECIO" ? "bg-amber-500 text-white"
    : "bg-red-600 text-white";
  const DecisionIcon = result?.decision === "COMPRAR" ? TrendingUp
    : result?.decision === "NEGOCIAR PRECIO" ? AlertTriangle
    : TrendingDown;

  return (
    <div className="max-w-screen-2xl mx-auto p-6 lg:p-8 space-y-6" data-testid="calculadora-page">
      <div>
        <h1 className="font-outfit font-bold text-3xl tracking-tight text-stone-900 flex items-center gap-2">
          <Sparkles className="w-7 h-7 text-[#1E3A8A]" strokeWidth={1.75} /> Centro de Inteligencia de Compra
        </h1>
        <p className="text-sm text-stone-500 mt-1">Asistente de decisión basado en histórico de la planta. Evalúa calidad, predice corrección y rendimiento, y recomienda comprar o no.</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        {/* INPUTS */}
        <Card className="xl:col-span-2 p-6 border-stone-200 bg-white shadow-sm h-fit">
          <h3 className="font-outfit font-semibold text-lg text-stone-900 mb-4">Datos de la pipa</h3>
          <form onSubmit={compute} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Box label="Litros *">
                <Input data-testid="calc-liters" type="number" step="0.01" value={form.liters} onChange={(e) => onLitersChange(e.target.value)} />
              </Box>
              <Box label="Peso (kg) *">
                <Input data-testid="calc-kg" type="number" step="0.01" value={form.weight_kg} onChange={(e) => onKgChange(e.target.value)} />
              </Box>
              <Box label="% Grasa *">
                <Input data-testid="calc-fat" type="number" step="0.01" value={form.fat_pct} onChange={(e) => setForm({ ...form, fat_pct: e.target.value })} />
              </Box>
              <Box label="% Proteína">
                <Input data-testid="calc-protein" type="number" step="0.01" value={form.protein_pct} onChange={(e) => setForm({ ...form, protein_pct: e.target.value })} placeholder="Opcional (2.9-3.5 típico)" />
              </Box>
              <Box label="Temperatura (°C)">
                <Input data-testid="calc-temp" type="number" step="0.1" value={form.temperature_c} onChange={(e) => setForm({ ...form, temperature_c: e.target.value })} />
              </Box>
              <Box label="pH" sub={phInfo && <><b className="text-[#1E3A8A]">→ {phInfo.acidity}°D</b> · {phInfo.dornic_range}</>}>
                <Input data-testid="calc-ph" type="number" step="0.01" value={form.ph} onChange={(e) => onPhChange(e.target.value)} />
              </Box>
              <Box label="Acidez (°D)">
                <Input type="number" step="0.1" value={form.acidity} onChange={(e) => setForm({ ...form, acidity: e.target.value })} placeholder={phInfo ? `Auto: ${phInfo.acidity}` : ""} />
              </Box>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600">Proveedor (opcional, mejora análisis)</Label>
              <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="calc-supplier"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div className="border-t border-stone-200 pt-4">
              <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600">Tipo de costo *</Label>
              <div className="grid grid-cols-3 gap-2 mt-1.5">
                {[
                  { v: "liter", l: "Por litro" },
                  { v: "kg", l: "Por kilo" },
                  { v: "total", l: "Total" },
                ].map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    data-testid={`calc-cost-unit-${o.v}`}
                    onClick={() => setForm({ ...form, cost_unit: o.v })}
                    className={`text-xs font-ibm-plex py-2 rounded-md border transition-colors ${
                      form.cost_unit === o.v
                        ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                        : "bg-white text-stone-700 border-stone-200 hover:bg-stone-50"
                    }`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {form.cost_unit !== "total" ? (
                <Box label={`Costo unitario ($/${form.cost_unit === "liter" ? "L" : "kg"}) *`} sub={<>× {form.cost_unit === "liter" ? form.liters || 0 : form.weight_kg || 0}</>}>
                  <Input data-testid="calc-unit-cost" type="number" step="0.01" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} />
                </Box>
              ) : <div />}
              <Box label="Costo total compra ($)" sub={<>Auto-calculado</>}>
                <Input data-testid="calc-cost" type="number" step="0.01" value={form.purchase_cost} onChange={(e) => setForm({ ...form, purchase_cost: e.target.value })} disabled={form.cost_unit !== "total"} className={form.cost_unit !== "total" ? "bg-stone-50" : ""} />
              </Box>
            </div>

            <div className="grid grid-cols-2 gap-3 border-t border-stone-200 pt-4">
              <Box label="Precio crema ($/kg) *">
                <Input data-testid="calc-cream-price" type="number" step="0.01" value={form.cream_price} onChange={(e) => setForm({ ...form, cream_price: e.target.value })} />
              </Box>
              <Box label="Precio cuajada ($/kg) *">
                <Input data-testid="calc-curd-price" type="number" step="0.01" value={form.curd_price} onChange={(e) => setForm({ ...form, curd_price: e.target.value })} />
              </Box>
              <Box label="Rend. cuajada (%)">
                <Input type="number" step="0.1" value={form.curd_yield_pct} onChange={(e) => setForm({ ...form, curd_yield_pct: e.target.value })} />
              </Box>
              <Box label="Costo corrección manual ($)" sub="Vacío = usa histórico">
                <Input type="number" step="0.01" value={form.correction_cost_estimate} onChange={(e) => setForm({ ...form, correction_cost_estimate: e.target.value })} />
              </Box>
            </div>

            <Button type="submit" disabled={loading} data-testid="calc-submit-btn" className="w-full bg-[#1E3A8A] hover:bg-[#1E40AF]">
              {loading ? "Analizando..." : "Analizar compra"}
            </Button>
          </form>
        </Card>

        {/* RESULTS */}
        <div className="xl:col-span-3 space-y-4">
          {!result ? (
            <Card className="p-12 border-stone-200 bg-stone-50/50 border-dashed text-center">
              <Sparkles className="w-10 h-10 mx-auto text-stone-300 mb-3" strokeWidth={1.5} />
              <p className="text-sm text-stone-500">Captura los datos y presiona "Analizar compra" para ver el análisis completo.</p>
            </Card>
          ) : (
            <>
              {/* DECISION */}
              <Card data-testid="calc-recommendation" className={`p-6 ${decisionColor(result.decision)} shadow-sm`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <DecisionIcon className="w-8 h-8" strokeWidth={1.75} />
                    <div>
                      <div className="text-xs uppercase tracking-[0.15em] font-outfit font-semibold opacity-80">Decisión</div>
                      <div className="text-3xl font-outfit font-bold mt-1">{result.decision}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs uppercase tracking-wider font-outfit font-semibold opacity-80">Margen</div>
                    <div className="text-2xl font-outfit font-bold tabular-nums">{result.margin_pct}%</div>
                    <div className="text-xs opacity-80 mt-0.5">Utilidad: ${result.adjusted_profit.toLocaleString("es-MX")}</div>
                  </div>
                </div>
                <p className="text-sm mt-4 opacity-95 leading-relaxed">{result.conclusion}</p>
              </Card>

              {/* QUALITY SEMAFORO */}
              <Card className="p-5 border-stone-200 bg-white shadow-sm">
                <QualityOverallBanner overall={result.quality.overall} similar_count={result.similar_count} />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                  <QualityCard label="Grasa" info={result.quality.fields.fat_pct} />
                  <QualityCard label="pH" info={result.quality.fields.ph} />
                  <QualityCard label="Acidez" info={result.quality.fields.acidity} />
                  <QualityCard label="Temperatura" info={result.quality.fields.temperature_c} />
                  {result.protein_quality && (
                    <QualityCard label="Proteína" info={result.protein_quality} />
                  )}
                </div>
              </Card>

              {/* ALERTS */}
              {result.alerts && result.alerts.length > 0 && (
                <Card className="p-5 border-amber-200 bg-amber-50/50 shadow-sm">
                  <h3 className="font-outfit font-semibold text-amber-900 mb-2 flex items-center gap-2"><Bell className="w-4 h-4" /> Alertas</h3>
                  <ul className="space-y-1.5">
                    {result.alerts.map((a, i) => (
                      <li key={i} className="text-sm text-amber-900 flex items-start gap-2">
                        <span className="text-amber-600 mt-0.5">⚠</span> {a}
                      </li>
                    ))}
                  </ul>
                </Card>
              )}

              {/* RENTABILIDAD */}
              <Card className="p-5 border-stone-200 bg-white shadow-sm">
                <h3 className="font-outfit font-semibold text-stone-900 mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Análisis de rentabilidad</h3>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <Row label="Puntos de grasa" value={result.fat_points.toLocaleString("es-MX")} />
                  <Row label="Valor crema" value={`$${result.cream_value.toLocaleString("es-MX", {minimumFractionDigits:2})}`} />
                  <Row label="Leche descremada" value={`${result.skim_milk.toLocaleString("es-MX")} L`} />
                  <Row label="Cuajada estimada" value={`${result.curd_kg.toLocaleString("es-MX")} kg`} />
                  <Row label="Valor cuajada" value={`$${result.curd_value.toLocaleString("es-MX", {minimumFractionDigits:2})}`} />
                  <Row label="Ingreso total" value={`$${result.total_income.toLocaleString("es-MX", {minimumFractionDigits:2})}`} bold />
                  <Row label="Costo compra" value={`-$${result.inputs.purchase_cost_resolved.toLocaleString("es-MX", {minimumFractionDigits:2})}`} />
                  <Row label="Costo corrección" value={`-$${result.correction_cost.toLocaleString("es-MX", {minimumFractionDigits:2})}`} />
                  <div className="col-span-2 border-t border-stone-200 mt-2 pt-2">
                    <Row label="Utilidad ajustada" value={`$${result.adjusted_profit.toLocaleString("es-MX", {minimumFractionDigits:2})}`} bold highlight />
                  </div>
                </div>
              </Card>

              {/* PREDICTIONS */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="p-5 border-stone-200 bg-white shadow-sm">
                  <h3 className="font-outfit font-semibold text-stone-900 mb-2">Predicción de corrección</h3>
                  <div className="text-xs text-stone-500 mb-3">Basado en {result.pred_correction.samples} lote(s) similar(es) · confianza <b className="text-stone-700">{result.pred_correction.confidence}</b></div>
                  <div className="space-y-1.5 text-sm">
                    <Row label="Base prom." value={`${result.pred_correction.avg_base_kg} kg`} />
                    <Row label="Caseína prom." value={`${result.pred_correction.avg_caseina_kg} kg`} />
                    <Row label="Costo prom. total" value={`$${result.pred_correction.avg_total_cost.toLocaleString("es-MX")}`} bold />
                  </div>
                </Card>

                <Card className="p-5 border-stone-200 bg-white shadow-sm">
                  <h3 className="font-outfit font-semibold text-stone-900 mb-2">Predicción de rendimiento</h3>
                  <div className="text-xs text-stone-500 mb-3">{result.pred_yield.samples} producciones similares</div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-500 mb-1">Crema (%)</div>
                      <Row label="Promedio" value={result.pred_yield.cream.avg ?? "—"} />
                      <Row label="Mejor" value={result.pred_yield.cream.best ?? "—"} />
                      <Row label="Peor" value={result.pred_yield.cream.worst ?? "—"} />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-500 mb-1">Cuajada (%)</div>
                      <Row label="Promedio" value={result.pred_yield.curd.avg ?? "—"} />
                      <Row label="Mejor" value={result.pred_yield.curd.best ?? "—"} />
                      <Row label="Peor" value={result.pred_yield.curd.worst ?? "—"} />
                    </div>
                  </div>
                </Card>
              </div>

              {/* SUPPLIER */}
              {result.supplier_analysis && (
                <Card className="p-5 border-stone-200 bg-white shadow-sm">
                  <h3 className="font-outfit font-semibold text-stone-900 mb-3">Análisis del proveedor: {result.supplier_analysis.name}</h3>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div><div className="text-xs text-stone-500">Compras hist.</div><div className="font-outfit font-semibold text-xl text-stone-900">{result.supplier_analysis.total_purchases}</div></div>
                    <div><div className="text-xs text-stone-500">Grasa prom.</div><div className="font-outfit font-semibold text-xl text-stone-900">{result.supplier_analysis.avg_fat_pct ?? "—"}%</div></div>
                    <div><div className="text-xs text-stone-500">Acidez prom.</div><div className="font-outfit font-semibold text-xl text-stone-900">{result.supplier_analysis.avg_acidity ?? "—"}°D</div></div>
                  </div>
                </Card>
              )}

              {/* HISTORICAL TABLE */}
              {result.history_table && result.history_table.length > 0 && (
                <Card className="border-stone-200 bg-white shadow-sm overflow-hidden">
                  <div className="px-5 py-3 border-b border-stone-200 flex items-center gap-2">
                    <History className="w-4 h-4 text-stone-600" />
                    <h3 className="font-outfit font-semibold text-stone-900">Lotes históricos similares</h3>
                    <span className="text-xs text-stone-500 ml-auto">Promedio: grasa {result.history_avg.fat_pct}%, pH {result.history_avg.ph}, acidez {result.history_avg.acidity}</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-stone-50 border-b border-stone-200">
                        <tr>{["Fecha", "Proveedor", "Grasa", "pH", "Acidez", "$ Corr.", "Rend. Crema", "Rend. Cuajada"].map((h) =>
                          <th key={h} className="px-3 py-2 text-left text-[10px] font-outfit font-semibold uppercase tracking-wider text-stone-600">{h}</th>
                        )}</tr>
                      </thead>
                      <tbody>
                        {result.history_table.map((h, i) => (
                          <tr key={i} className="border-b border-stone-100">
                            <td className="px-3 py-2 text-stone-700">{h.date}</td>
                            <td className="px-3 py-2 text-stone-700">{h.supplier}</td>
                            <td className="px-3 py-2 tabular-nums">{h.fat_pct ? `${h.fat_pct}%` : "—"}</td>
                            <td className="px-3 py-2 tabular-nums">{h.ph ?? "—"}</td>
                            <td className="px-3 py-2 tabular-nums">{h.acidity ?? "—"}</td>
                            <td className="px-3 py-2 tabular-nums">{h.correction_cost ? `$${h.correction_cost}` : "—"}</td>
                            <td className="px-3 py-2 tabular-nums text-emerald-700">{h.cream_yield_pct ? `${h.cream_yield_pct}%` : "—"}</td>
                            <td className="px-3 py-2 tabular-nums text-emerald-700">{h.curd_yield_pct ? `${h.curd_yield_pct}%` : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const Box = ({ label, sub, children }) => (
  <div>
    <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600">{label}</Label>
    <div className="mt-1.5">{children}</div>
    {sub && <p className="text-[10px] text-stone-500 mt-1">{sub}</p>}
  </div>
);

const Row = ({ label, value, bold, highlight }) => (
  <div className="flex justify-between items-center">
    <span className="text-stone-600">{label}</span>
    <span className={`tabular-nums font-ibm-plex ${bold ? "font-semibold" : ""} ${highlight ? "text-[#1E3A8A] text-base" : "text-stone-900"}`}>{value}</span>
  </div>
);
